import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';

export const InputWrapper = styled('div')`
	display: inline-flex;
	width: 100%;
`;

export const IconWrapper = styled('span')`
	display: inline-flex;
	align-items: center;
	justify-content: center;
`;

// Accept an optional theme so callers can pass it in from the styled() callback
const baseStyles = (height: number, fontSize: number, radius = 12, theme?: any) => ({
	'& .MuiInputBase-root': {
		height,
		borderRadius: radius,
		// Provide a subtle input background for dark mode so the outline is visible on pure black
		backgroundColor: theme && theme.palette?.mode === 'dark' ? theme.palette.background?.paper || 'rgba(255,255,255,0.02)' : undefined,
	},
	'& .MuiInputBase-input': {
		fontSize,
		padding: '0 12px',
		color: theme && theme.palette?.mode === 'dark' ? theme.palette.text?.primary || '#fff' : undefined,
	},
	'& .MuiFormLabel-root': {
		fontSize: Math.max(fontSize - 2, 12),
		color: theme && theme.palette?.mode === 'dark' ? theme.palette.text?.secondary || 'rgba(255,255,255,0.7)' : undefined,
	},
	// Outline / border adjustments (important for black backgrounds)
	'& .MuiOutlinedInput-notchedOutline': {
		borderColor: theme && theme.palette?.mode === 'dark' ? 'rgba(255,255,255,0.12)' : undefined,
	},
	'&:hover .MuiOutlinedInput-notchedOutline': {
		borderColor: theme && theme.palette?.mode === 'dark' ? 'rgba(255,255,255,0.18)' : undefined,
	},
	// when focused keep the outline visible and slightly brighter
	'& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
		borderColor: theme && theme.palette?.mode === 'dark' ? theme.palette.primary?.light || 'rgba(255,255,255,0.6)' : undefined,
	},
	// label color when focused
	'& .MuiInputLabel-root.Mui-focused': {
		color: theme && theme.palette?.mode === 'dark' ? theme.palette.primary?.light || 'rgba(255,255,255,0.9)' : undefined,
	},
});

const byState = {
	booking: (theme: any) => ({
		'& .MuiOutlinedInput-notchedOutline': {
			borderColor: theme.palette.primary.main,
		},
		'&:hover .MuiOutlinedInput-notchedOutline': {
			borderColor: theme.palette.primary.dark,
		},
		'& .MuiInputBase-input': {
			fontWeight: 600,
		},
	}),
	disabled: (theme: any) => ({
		'& .MuiInputBase-root.Mui-disabled': {
			backgroundColor: theme.palette.action.disabledBackground,
		},
		'& .MuiFormLabel-root.Mui-disabled': {
			color: theme.palette.text.disabled,
		},
	}),
};

// Normal
export const StyledInputSmall = styled(TextField)(({ theme }) => ({
	...baseStyles(36, 14, 12, theme),
}));
export const StyledInputMedium = styled(TextField)(({ theme }) => ({
	...baseStyles(44, 16, 12, theme),
}));
export const StyledInputLarge = styled(TextField)(({ theme }) => ({
	...baseStyles(52, 18, 14, theme),
}));

// Booking-accented
export const BookingInputSmall = styled(TextField)(({ theme }) => ({
	...baseStyles(36, 14, 12, theme),
	...byState.booking(theme),
}));
export const BookingInputMedium = styled(TextField)(({ theme }) => ({
	...baseStyles(44, 16, 12, theme),
	...byState.booking(theme),
}));
export const BookingInputLarge = styled(TextField)(({ theme }) => ({
	...baseStyles(52, 18, 14, theme),
	...byState.booking(theme),
}));

// Disabled variants (for parity with CTA naming)
export const DisabledInputSmall = styled(TextField)(({ theme }) => ({
	...baseStyles(36, 14, 12, theme),
	...byState.disabled(theme),
}));
export const DisabledInputMedium = styled(TextField)(({ theme }) => ({
	...baseStyles(44, 16, 12, theme),
	...byState.disabled(theme),
}));
export const DisabledInputLarge = styled(TextField)(({ theme }) => ({
	...baseStyles(52, 18, 14, theme),
	...byState.disabled(theme),
}));
